import clsx from 'clsx';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { fmtDateWeek, fmtMonthDay, type FollowUpDTO, type ProjectDTO, type ReportItemDTO, type ReportPageDTO } from '@wd/shared';
import { timeCN } from '../../lib/clock';
import { useMe } from '../../lib/queries';
import { Icon, StatusIcon } from '../../ui/icons';
import { AIAvatar, Avatar, BottomSheet, Button, Checkbox, Message, SheetItem, WithVague } from '../../ui/primitives';
import { durationText, itemMeta, type ReportEditor } from './useReportEditor';
import { ItemFiles } from './ItemFiles';

export function ReportMobile({ report, ed, today }: { report: ReportPageDTO; ed: ReportEditor; today: string }) {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sheetFor, setSheetFor] = useState<ReportItemDTO | null>(null);
  const [feedbackFor, setFeedbackFor] = useState<ReportItemDTO | null>(null);
  const replyRef = useRef<HTMLInputElement>(null);
  const isToday = report.date === today;
  const readoutById = useMemo(() => new Map(report.readouts.map((p) => [p.id, p])), [report.readouts]);

  const aiByItem = useMemo(() => {
    const m = new Map<string, FollowUpDTO>();
    for (const f of report.aiFollowUps) if (f.reportItemId) m.set(f.reportItemId, f);
    return m;
  }, [report.aiFollowUps]);
  const answeredCount = report.aiFollowUps.filter((f) => f.answer).length;

  const meta = (() => {
    if (report.status === 'submitted') return [`${report.items.length} 个项目`, `已同步给${report.submitTo.teamName ?? report.submitTo.managerName ?? '主管'}`];
    if (report.prefillSource === 'none' && report.recent.length === 0) return ['第一份日报', `${report.items.length} 个项目`];
    const parts = [report.prefillSource === 'plan' ? 'AI 已预填' : '没有可预填的计划', `${report.items.length} 个项目`];
    if (answeredCount) parts.push(`已回复 ${answeredCount} 条追问`);
    else if (report.overdue) parts.push('确认无误可直接提交');
    else parts.push(timeCN(report.updatedAt));
    return parts;
  })();

  const firstDay = report.prefillSource === 'none' && report.recent.length === 0 && report.status === 'draft';
  const replyPlaceholder = ed.replyTarget ? (ed.replyTarget.kind === 'manager' ? `回复${ed.replyTarget.fromName}…` : '回复追问…') : '补充说明…';

  return (
    <div className="rm">
      {report.status === 'submitted' && (
        <div className="rm-banner" role="status">
          <StatusIcon status="done" />
          <span className="flex1">
            已提交 · {report.submittedAt ? timeCN(report.submittedAt) : ''}
            {report.late ? ' · 逾期提交' : ''}
          </span>
          {report.canRetract && (
            <Button mobile variant="text" onClick={() => void ed.retract()}>
              撤回修改
            </Button>
          )}
        </div>
      )}
      {report.overdue && (
        <div className="rm-banner" role="status">
          <Icon name="clock" size={14} />
          <span className="flex1">今天的日报还没提交</span>
          <span style={{ fontSize: 13, color: 'var(--text-4)', paddingRight: 12 }}>已过截止 {durationText(-(ed.minutesLeft ?? 0))}</span>
        </div>
      )}
      {!isToday && (
        <div className="rm-banner">
          <Icon name="cal" size={14} />
          <span className="flex1">{fmtDateWeek(report.date)} 的日报，只读</span>
          <Button mobile variant="text" onClick={() => navigate('/')}>
            回到今天
          </Button>
        </div>
      )}

      <div className="rm-scroll">
        <div className="rm-head">
          <div className="title">{fmtDateWeek(report.date)}</div>
          <div className="meta">
            {meta.map((m, i) => (
              <span key={i} className="row" style={{ gap: 6 }}>
                {i > 0 && <span>·</span>}
                <span>{m}</span>
              </span>
            ))}
          </div>
        </div>

        {ed.pendingManager.map((f) => (
          <div key={f.id} style={{ marginBottom: 12 }}>
            <div className="rm-label">
              <span>来自{f.fromName}的追问</span>
              <span>
                {f.projectCode}
                {f.aboutDate ? ` · ${fmtMonthDay(f.aboutDate)}` : ''}
              </span>
            </div>
            <div className="rm-ask">
              <Message avatar={<Avatar initial={f.fromInitial} seed={f.fromName} size={24} />} name={f.fromName} meta={timeCN(f.askedAt)}>
                {f.question}
              </Message>
              <MobileChoices f={f} ed={ed} onWrite={() => replyRef.current?.focus()} writeLabel="自己写…" />
            </div>
          </div>
        ))}

        {firstDay && (
          <div className="rm-ask" style={{ marginBottom: 8 }}>
            <Message avatar={<AIAvatar size={24} />} name="日报助手" meta={timeCN(report.updatedAt)}>
              第一天没有可参考的计划，每个项目写一两句今天做了什么就行。明天起我会按你今天写的「明日计划」来预填。
            </Message>
          </div>
        )}

        <div className="rm-label">
          <span>项目进展</span>
          {ed.editedCount > 0 && report.status === 'draft' && <span>修改了 {ed.editedCount} 处</span>}
        </div>
        {report.items.map((it, i) => (
          <div key={it.id}>
            {i > 0 && <div className="rm-hr" />}
            <MobileRow
              item={it}
              ed={ed}
              project={readoutById.get(it.projectId)}
              followUp={aiByItem.get(it.id)}
              expanded={expanded === it.id}
              onTap={() => ed.editable && setExpanded(expanded === it.id ? null : it.id)}
              onLongPress={() => ed.editable && setSheetFor(it)}
              onClose={() => setExpanded(null)}
            />
            <ItemFiles item={it} editable={ed.editable} showAdd={expanded === it.id} className="rm-files" />
          </div>
        ))}

        {report.aiFollowUps.filter((f) => !f.skipped).map((f) => (
          <div key={f.id} style={{ marginTop: 16 }}>
            <div className="rm-label">
              <span>AI 追问</span>
            </div>
            <div className="rm-ask">
              <Message avatar={<AIAvatar size={24} />} name="日报助手" meta={f.answer ? timeCN(f.askedAt) : `关于 ${f.projectCode}`}>
                {f.question}
              </Message>
              {f.answer ? (
                <div style={{ marginTop: 14 }}>
                  <Message avatar={<Avatar initial={me?.initial ?? '我'} seed={me?.name} size={24} />} name={me?.name ?? '我'} meta={f.answeredAt ? timeCN(f.answeredAt) : ''}>
                    {f.answer}
                  </Message>
                  <div className="t-meta" style={{ margin: '6px 0 0 36px' }}>
                    已写入 {f.projectCode} 今天的进展
                  </div>
                </div>
              ) : (
                ed.editable && <MobileChoices f={f} ed={ed} onWrite={() => replyRef.current?.focus()} writeLabel="都不是，我来写" />
              )}
            </div>
            {!f.answer && report.overdue && <div className="rm-tip">不确定也可以先提交，明天再补</div>}
          </div>
        ))}

        {(ed.editable || report.plans.length > 0) && <MobilePlans report={report} ed={ed} />}

        {!ed.editable && report.note && (
          <>
            <div className="rm-label" style={{ marginTop: 16 }}>
              <span>补充说明</span>
            </div>
            <div className="t-body" style={{ padding: '0 20px', whiteSpace: 'pre-wrap' }}>
              {report.note}
            </div>
          </>
        )}

        {ed.notice && <div className="rm-tip">{ed.notice}</div>}
        {ed.error && (
          <div className="rm-tip c-red" role="alert">
            {ed.error}
          </div>
        )}
      </div>

      {ed.editable && (
        <div className="rm-reply">
          <Button
            mobile
            variant="icon"
            aria-label="语音输入"
            title="点输入框后，用输入法的语音键说话"
            onClick={() => {
              replyRef.current?.focus();
              ed.setNotice('点输入框后，用输入法的语音键说话');
            }}
          >
            <Icon name="mic" size={22} color="var(--text-3)" />
          </Button>
          <input
            ref={replyRef}
            value={ed.replyTarget ? ed.reply : ed.note}
            onChange={(e) => (ed.replyTarget ? ed.setReply(e.target.value) : ed.setNote(e.target.value))}
            placeholder={replyPlaceholder}
            enterKeyHint={ed.replyTarget ? 'send' : 'done'}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                if (ed.replyTarget) void ed.sendReply();
                else (e.target as HTMLInputElement).blur();
              }
            }}
            aria-label={replyPlaceholder}
          />
          {ed.replyTarget && ed.reply.trim() ? (
            <Button mobile variant="primary" onClick={() => void ed.sendReply()}>
              发送
            </Button>
          ) : (
            <Button mobile variant="primary" disabled={!ed.hasContent || ed.actions.submit.isPending} onClick={() => void ed.submit()}>
              提交
            </Button>
          )}
        </div>
      )}

      {sheetFor && (
        <ItemSheet
          item={sheetFor}
          followUp={aiByItem.get(sheetFor.id)}
          ed={ed}
          onClose={() => setSheetFor(null)}
          onEdit={() => {
            setExpanded(sheetFor.id);
            setSheetFor(null);
          }}
          onFeedback={() => {
            setFeedbackFor(sheetFor);
            setSheetFor(null);
          }}
        />
      )}
      {feedbackFor && <FeedbackSheet item={feedbackFor} ed={ed} onClose={() => setFeedbackFor(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------

function MobileRow({
  item,
  ed,
  project,
  followUp,
  expanded,
  onTap,
  onLongPress,
  onClose,
}: {
  item: ReportItemDTO;
  ed: ReportEditor;
  project: ProjectDTO | undefined;
  followUp: FollowUpDTO | undefined;
  expanded: boolean;
  onTap: () => void;
  onLongPress: () => void;
  onClose: () => void;
}) {
  const text = ed.textOf(item);
  const health = project?.health.find((h) => h.kind !== 'owner_on_leave');
  const press = useLongPress(onLongPress);
  const meta = itemMeta(item, text, followUp);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = '0px';
    el.style.height = `${Math.max(el.scrollHeight, 72)}px`;
  }, [expanded, text]);
  useEffect(() => {
    if (expanded) taRef.current?.focus();
  }, [expanded]);

  return (
    <>
      <button type="button" className="rm-row" onClick={() => !press.fired() && onTap()} {...press.handlers}>
        <StatusIcon status={item.projectStatus} />
        <span className="info">
          <span className="title">
            <span className="name">{item.projectName}</span>
            <span className="code">{item.projectCode}</span>
          </span>
          {!expanded && (
            <span className="desc">
              {item.noProgress ? '今天没有进展' : text.trim() ? <WithVague text={text} phrase={item.vaguePhrase} /> : '今天在这个项目上做了什么？'}
            </span>
          )}
        </span>
        {health ? (
          <span className="right c-red">{health.label}</span>
        ) : !text.trim() && !item.noProgress ? (
          <span className="right">未填写</span>
        ) : project?.dueDate ? (
          <span className="right">{fmtMonthDay(project.dueDate)}</span>
        ) : null}
      </button>
      {expanded && (
        <div className="rm-edit">
          <textarea
            ref={taRef}
            value={text}
            placeholder="今天在这个项目上做了什么？"
            onChange={(e) => ed.setItemText(item.id, e.target.value)}
            aria-label={`${item.projectName}的今日进展`}
          />
          <div className="bar">
            <span className="t-meta flex1">{meta?.label === '已修改' ? 'AI 预填 · 已修改' : meta?.label === '预填' ? 'AI 预填' : ''}</span>
            <Button
              mobile
              variant="text"
              onClick={() => {
                ed.revert(item.id);
                onClose();
              }}
            >
              取消
            </Button>
            <Button
              variant="primary"
              style={{ height: 36 }}
              onClick={() => {
                void ed.flush();
                onClose();
              }}
            >
              完成
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

function useLongPress(cb: () => void, ms = 480) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firedRef = useRef(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  return {
    fired: () => {
      const f = firedRef.current;
      firedRef.current = false;
      return f;
    },
    handlers: {
      onPointerDown: (e: React.PointerEvent) => {
        firedRef.current = false;
        start.current = { x: e.clientX, y: e.clientY };
        clear();
        timer.current = setTimeout(() => {
          firedRef.current = true;
          cb();
        }, ms);
      },
      onPointerMove: (e: React.PointerEvent) => {
        if (!start.current) return;
        const dx = e.clientX - start.current.x;
        const dy = e.clientY - start.current.y;
        // 左滑同样打开操作面板
        if (dx < -50 && Math.abs(dy) < 30) {
          clear();
          firedRef.current = true;
          start.current = null;
          cb();
        } else if (Math.abs(dy) > 10) clear();
      },
      onPointerUp: clear,
      onPointerCancel: clear,
      onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
    },
  };
}

function MobileChoices({ f, ed, onWrite, writeLabel }: { f: FollowUpDTO; ed: ReportEditor; onWrite: () => void; writeLabel: string }) {
  return (
    <div style={{ marginTop: 10, paddingLeft: 36 }}>
      {f.suggestedAnswers.slice(0, 2).map((a) => (
        <button key={a} type="button" className="rm-choice" onClick={() => void ed.answer(f, a)}>
          {a}
        </button>
      ))}
      <button type="button" className="rm-choice" style={{ color: 'var(--text-4)' }} onClick={onWrite}>
        {writeLabel}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------

function MobilePlans({ report, ed }: { report: ReportPageDTO; ed: ReportEditor }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);
  const projectId = report.items[0]?.projectId ?? null;
  const add = async () => {
    if (!text.trim()) return setAdding(false);
    try {
      await ed.actions.addPlan.mutateAsync({ reportId: report.id, projectId, text });
      setText('');
      setAdding(false);
    } catch (e) {
      ed.setError(e instanceof Error ? e.message : String(e));
    }
  };
  return (
    <div style={{ marginTop: 16 }}>
      <div className="rm-label">
        <span>明日计划 {report.plans.length} 项</span>
      </div>
      {report.plans.map((p) => (
        <button
          key={p.id}
          type="button"
          className={clsx('rm-plan', !p.checked && 'off')}
          disabled={!ed.editable}
          onClick={() => ed.actions.updatePlan.mutate({ id: p.id, checked: !p.checked })}
        >
          <Checkbox checked={p.checked} large />
          <span className="t">{p.text}</span>
          {p.suggestedByAI && <span className="pc">AI 建议</span>}
          <span className="pc">{p.projectCode ?? ''}</span>
        </button>
      ))}
      {ed.editable &&
        (adding ? (
          <div className="rm-plan">
            <Icon name="plus" size={18} />
            <input
              ref={inputRef}
              className="wd-input mobile"
              value={text}
              placeholder="明天要做的事"
              enterKeyHint="done"
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !e.nativeEvent.isComposing && void add()}
              onBlur={() => void add()}
            />
          </div>
        ) : (
          <button type="button" className="rm-plan" onClick={() => setAdding(true)}>
            <Icon name="plus" size={18} />
            <span className="t c4">{report.plans.length ? '添加' : '添加明天要做的事'}</span>
          </button>
        ))}
      {ed.editable && report.plans.length > 0 && <div className="rm-tip">勾选的计划明天会预填进日报；没做完的，AI 会问一句原因。</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 纠正 AI（设计稿 11c）

function ItemSheet({
  item,
  followUp,
  ed,
  onClose,
  onEdit,
  onFeedback,
}: {
  item: ReportItemDTO;
  followUp: FollowUpDTO | undefined;
  ed: ReportEditor;
  onClose: () => void;
  onEdit: () => void;
  onFeedback: () => void;
}) {
  const [confirmExit, setConfirmExit] = useState(false);
  if (confirmExit) {
    return (
      <BottomSheet onClose={onClose}>
        <div style={{ padding: '4px 20px 12px' }}>
          <div className="t-block-title">确认退出 {item.projectName}？</div>
          <div className="rm-tip" style={{ padding: 0 }}>
            会请项目负责人确认。确认前这个项目仍保留在你的日报里。
          </div>
        </div>
        <div className="row" style={{ gap: 8, padding: '8px 20px 0' }}>
          <Button mobile variant="ghost" full onClick={onClose}>
            取消
          </Button>
          <Button
            mobile
            variant="primary"
            full
            onClick={async () => {
              try {
                await ed.actions.requestExit.mutateAsync({ projectId: item.projectId, reason: '这个项目不归我了', handoverToId: null });
                ed.setNotice('已提交退出申请，等项目负责人确认');
              } catch (e) {
                ed.setError(e instanceof Error ? e.message : String(e));
              }
              onClose();
            }}
          >
            提交申请
          </Button>
        </div>
      </BottomSheet>
    );
  }
  return (
    <BottomSheet onClose={onClose}>
      <div className="row" style={{ gap: 8, padding: '4px 20px 8px' }}>
        <span className="c1" style={{ fontSize: 15 }}>
          {item.projectName}
        </span>
        <span className="t-meta">{item.projectCode}</span>
      </div>
      <SheetItem icon="edit" title="编辑这条进展" onClick={onEdit} />
      <SheetItem
        icon="minus"
        title={item.noProgress ? '取消“今天没有进展”' : '今天没有进展'}
        sub="如实记录，不计入“说了没做”"
        onClick={() => {
          ed.actions.updateItem.mutate({ id: item.id, noProgress: !item.noProgress });
          onClose();
        }}
      />
      {followUp && !followUp.answer && !followUp.skipped && (
        <SheetItem
          icon="skip"
          title="跳过这条追问"
          sub="今天不再追问这个项目，明天照常"
          onClick={() => {
            void ed.skip(followUp);
            onClose();
          }}
        />
      )}
      <SheetItem icon="reply" title="预填内容不对" sub="告诉 AI 哪里写错了，用于改进下次预填" onClick={onFeedback} />
      <SheetItem icon="out" title="这个项目不归我了" sub="会请项目负责人确认，确认前仍保留" onClick={() => setConfirmExit(true)} />
      <div style={{ padding: '8px 20px 0' }}>
        <Button mobile variant="ghost" full style={{ width: '100%' }} onClick={onClose}>
          取消
        </Button>
      </div>
    </BottomSheet>
  );
}

const FEEDBACK_REASONS = [
  { value: 'wrong_progress', label: '进展写错了' },
  { value: 'wrong_project', label: '不是这个项目的事' },
  { value: 'others_work', label: '这是别人做的' },
  { value: 'too_vague', label: '写得太笼统' },
  { value: 'other', label: '其他' },
] as const;

function FeedbackSheet({ item, ed, onClose }: { item: ReportItemDTO; ed: ReportEditor; onClose: () => void }) {
  const [reason, setReason] = useState<string>('wrong_progress');
  const [text, setText] = useState('');
  return (
    <BottomSheet onClose={onClose}>
      <div style={{ padding: '4px 20px 8px' }}>
        <div className="t-block-title">预填内容哪里不对？</div>
        <div className="rm-tip" style={{ padding: 0 }}>
          {item.projectCode} · 只用于改进下次预填，不会给主管看
        </div>
      </div>
      {FEEDBACK_REASONS.map((r) => (
        <button key={r.value} type="button" className="wd-sheet-item" onClick={() => setReason(r.value)}>
          <span className={clsx('wd-radio lg', reason === r.value && 'on')} />
          <span className="t">{r.label}</span>
        </button>
      ))}
      <div style={{ padding: '4px 20px 0' }}>
        <input className="wd-input mobile" placeholder="补充一句（可选）" value={text} onChange={(e) => setText(e.target.value)} />
      </div>
      <div className="row" style={{ gap: 8, padding: '12px 20px 0' }}>
        <Button mobile variant="ghost" full onClick={onClose}>
          取消
        </Button>
        <Button
          mobile
          variant="primary"
          full
          onClick={() => {
            ed.actions.feedback.mutate({ itemId: item.id, reason, text: text.trim() || null });
            ed.setNotice('收到，下次预填会参考');
            onClose();
          }}
        >
          发送
        </Button>
      </div>
    </BottomSheet>
  );
}
